import React, { useState } from 'react';
import styled from 'styled-components';
import { DialogClearStylesButtonsFooter, DialogFooter } from 'components/accounts/DialogClearStyles';
import { PrimaryButton, SecondaryButton } from 'components/v2/buttons';
import WizardTextInputWithAutoComplete from 'components/v2/epd-wizard-inputs/WizardTextInputWithAutoComplete';
import { HelpBox } from 'components/v2/help-boxes';
import AdminSearchCompaniesQueryHelper from 'pages/admin/companies/search/AdminSearchCompaniesQueryHelper';
import { postSearchCompanies } from 'services/api/adminApi';
import { useToolCompaniesSearch } from 'services/api/adminToolsQueriesAndMutations';
import { AdminCompanySearchRequest } from 'types/admin-search-companies/AdminCompanySearchRequest';
import { Option } from 'types/types';
import AdminEditToolHeadCompanySelectorFormResults from './AdminEditToolHeadCompanySelectorFormResults';
import { Skeleton } from 'primereact/skeleton';

interface Props {
  initialValue: Option | null;
  onSubmit(newValue: Option | null): void;
}

const DEFAULT_COMPANY_SEARCH_PARAMS = AdminSearchCompaniesQueryHelper.parse('');

const AdminEditToolHeadCompanySelectorForm: React.FC<Props> = ({ initialValue, onSubmit }) => {
  const [searchText, setSearchText] = useState<string>(initialValue?.label ?? '');
  const [autoCompleteList, setAutoCompleteList] = useState<string[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Option | null>(initialValue);
  const [searchParams, setSearchParams] = useState<AdminCompanySearchRequest>(() => {
    if (!initialValue) {
      return DEFAULT_COMPANY_SEARCH_PARAMS;
    }

    return {
      ...DEFAULT_COMPANY_SEARCH_PARAMS,
      name: initialValue.label!,
    };
  });

  const searchResultsQuery = useToolCompaniesSearch(searchParams);

  const handleSearch = (name: string) => {
    const newParams: AdminCompanySearchRequest = {
      ...DEFAULT_COMPANY_SEARCH_PARAMS,
      name,
    };

    setSearchParams(newParams);
  };

  return (
    <div>
      <HelpBox style={{ marginBottom: 30 }}>Please select the company you want to assign the tool to</HelpBox>

      <WizardTextInputWithAutoComplete
        name="companyName"
        label="Assigning the tool to an approved company"
        value={searchText}
        triggerOnChangedFor="keypress"
        onChanged={(_: string, value: string) => setSearchText(value)}
        onSelectSuggested={(value) => {
          setSearchText(value);
          handleSearch(value);
        }}
        onSubmit={() => handleSearch(searchText)}
        onBlur={() => handleSearch(searchText)}
        placeholder="Type here to search"
        completeMethod={async ({ query }) => {
          const result = await postSearchCompanies({
            ...DEFAULT_COMPANY_SEARCH_PARAMS,
            pageSize: 5,
            name: query,
          });
          setAutoCompleteList(result.items.map((x) => x.name));
        }}
        suggestions={autoCompleteList}
      />

      {(() => {
        if (searchResultsQuery.error) {
          const message = searchResultsQuery.error instanceof Error ? searchResultsQuery.error.message : 'Unknown error';
          return `Failed to load results: ${message}`;
        }

        const searchResult = searchResultsQuery.data;
        if (!searchResult) {
          return (
            <SearchResultsWrapper>
              {Array(DEFAULT_COMPANY_SEARCH_PARAMS.pageSize)
                .fill(0)
                .map((x, i) => (
                  <Skeleton key={i} height="50px" />
                ))}
            </SearchResultsWrapper>
          );
        }

        if (searchResult.totalCount === 0) {
          return 'No results found';
        }

        return (
          <SearchResultsWrapper>
            <AdminEditToolHeadCompanySelectorFormResults
              companies={searchResult.items}
              companyId={selectedCompany?.value ?? null}
              onCompanySelected={(c) => setSelectedCompany({ value: c.id, label: c.name })}
            />
          </SearchResultsWrapper>
        );
      })()}

      <DialogFooter>
        <DialogClearStylesButtonsFooter>
          <SecondaryButton type="button" label="Cancel" onClick={() => onSubmit(null)} />
          <PrimaryButton
            label="Add"
            onClick={() => onSubmit(selectedCompany)}
            disabled={!selectedCompany || selectedCompany.value === initialValue?.value}
          />
        </DialogClearStylesButtonsFooter>
      </DialogFooter>
    </div>
  );
};

export default AdminEditToolHeadCompanySelectorForm;

const SearchResultsWrapper = styled.div`
  display: grid;
  gap: 8px;
  margin-top: 8px;
  max-height: 40vh;
  overflow-y: auto;
`;
